import { useEffect, useRef, useState } from 'react';

// Haversine distance in metres between two {lat,lng} points.
function metersBetween(a, b) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Detects when the runner's current position enters the pass-radius of any
// as-yet-unpassed checkpoint. Fires `onPass(checkpoint, index)` once per
// checkpoint (a passed checkpoint stays passed for the rest of the session).
//
// Params:
//   checkpoints — Array<{ name?, lat, lng }>
//   currentPosition — { lat, lng } | null
//   radius (m) — default 3 m (per product spec)
//   onPass — callback fired once per checkpoint when passed
//
// Returns:
//   { passedIndices: Set<number>, distancesToUnpassed: number[] | null }
export function useCheckpointTracker({
  checkpoints,
  currentPosition,
  radius = 3,
  onPass,
} = {}) {
  const [passed, setPassed] = useState(() => new Set());
  const onPassRef = useRef(onPass);
  onPassRef.current = onPass;

  useEffect(() => {
    if (!currentPosition || !Array.isArray(checkpoints) || checkpoints.length === 0) return;
    setPassed((prev) => {
      let next = prev;
      for (let i = 0; i < checkpoints.length; i++) {
        if (next.has(i)) continue;
        const cp = checkpoints[i];
        if (typeof cp?.lat !== 'number' || typeof cp?.lng !== 'number') continue;
        const d = metersBetween(currentPosition, { lat: cp.lat, lng: cp.lng });
        if (d <= radius) {
          if (next === prev) next = new Set(prev);
          next.add(i);
          try { onPassRef.current?.(cp, i); } catch { /* noop */ }
        }
      }
      return next;
    });
  }, [currentPosition?.lat, currentPosition?.lng, checkpoints, radius]);

  return { passedIndices: passed };
}
